<template>
  <div
    :style="userColours"
    :class="[deck.appearance.isPNP ? 'printnplay' : '']"
  >
    <div class="no-print">
      <div v-if="isEditable || localBggLink" class="row bgglink pt-3">
        <div class="col">
          <h3>BoardGameGeek discussion</h3>
          <BGGLink
            v-model.trim="localBggLink"
            ref="bggLink"
            :isEditable="isEditable"
          />
        </div>
      </div>
      <div v-if="isEditable || localNote" class="row notes pt-3">
        <div class="col">
          <h3>Notes</h3>
          <b-form-group
            id="notes"
            v-if="isEditable"
            description="These notes will be publicly viewable."
            label-for="notes-textarea"
          >
            <b-form-textarea
              id="notes-textarea"
              v-model="localNote"
              placeholder="Enter notes"
              rows="3"
              max-rows="6"
            ></b-form-textarea>
          </b-form-group>
          <p v-else>{{ localNote }}</p>
        </div>
      </div>
      <!-- Printing and appearance sections -->
      <b-row>
        <b-col cols="12">
          <h3>
            Printing
            <small
              ><a href="#" @click.prevent="showPrint = !showPrint">{{
                showPrint ? "Hide" : "Show"
              }}</a></small
            >
          </h3>
          <b-collapse v-model="showPrint">
            <div>
              <p>
                When you print this page using your browser's "print" function,
                everything except the cards will be hidden, and the cards will
                be next to each other to make cutting them out easier.
              </p>
              <p>
                To ensure correct printing, be sure to
                <strong>set your paper orientation to "Landscape"</strong>. You
                can save to a file using the "Save to PDF" option in your print
                dialogue.
              </p>
              <p>
                <em>Firefox users note</em>: you will need to select "Print
                Background Colours" and "Print Background Images" for everything
                to appear correctly. This is in the "Print" dialogue on Macs and
                in the "Page Settings" dialogue on Windows.
              </p>
              <p>
                You can add card borders and/or crop marks using these
                checkboxes:
              </p>
            </div>
            <b-form-checkbox v-model="hasCropMarks">
              Print crop marks (semi-functional)
            </b-form-checkbox>
            <b-form-checkbox v-model="hasCardBorders">
              Print card borders
            </b-form-checkbox>
            <b-form-checkbox
              v-model="printCardBacks"
              :disabled="!localDeck.appearance.cardbackUrl"
            >
              Print card backs (Note: printing double-sided is very difficult,
              so this doesn't even attempt to line up fronts and backs)
            </b-form-checkbox>
          </b-collapse>
        </b-col>

        <b-col cols="12" class="deck-properties py-4">
          <h3>
            Appearance
            <small
              ><a href="#" @click.prevent="showAppearance = !showAppearance">{{
                showAppearance ? "Hide" : "Show"
              }}</a></small
            >
          </h3>

          <b-collapse v-model="showAppearance">
            <b-form-group
              label-for="zoom-control"
              label="Zoom"
              description="Does not affect print size"
            >
              <b-form-input
                type="range"
                id="zoom-control"
                min="1"
                max="2"
                step="any"
                v-model="zoom"
                number
              />
            </b-form-group>

            <div class="card-deck appearance">
              <div
                class="card"
                :class="[
                  deck.appearance.isPNP ? 'text-muted' : 'border-primary',
                ]"
                @click="localDeck.appearance.isPNP = false"
              >
                <h5 class="card-header">Production</h5>
                <div class="card-body">
                  <p class="card-text">
                    As close as possible to the real thing without having
                    <a href="https://oliverbarrett.com/" target="_blank"
                      >Oliver Barrett</a
                    >
                    do the art for you.
                  </p>
                  <div class="form-group">
                    <label> Highlight colour </label>
                    <input
                      v-model="localDeck.appearance.highlightColour"
                      :disabled="deck.appearance.isPNP"
                      class="form-control"
                      type="color"
                    />
                  </div>
                  <div class="form-group">
                    <label> Background pattern </label>
                    <SvgBackgroundPicker
                      :selectedPattern="deck.appearance.patternName"
                      :bgColour="deck.appearance.highlightColour"
                      @change:background="pattern = $event.backgroundImage"
                      @change:pattern="
                        $set(localDeck.appearance, 'patternName', $event)
                      "
                    ></SvgBackgroundPicker>
                    <small class="text-muted">
                      Patterns by
                      <a href="https://www.heropatterns.com/" target="_blank"
                        >Steve Schoger</a
                      >
                    </small>
                  </div>
                </div>
              </div>
              <div
                class="card"
                :class="[
                  deck.appearance.isPNP ? 'border-primary' : 'text-muted',
                ]"
                @click="localDeck.appearance.isPNP = true"
              >
                <h5 class="card-header">Print and play</h5>
                <div class="card-body">
                  <p class="card-text">
                    Something a little simpler, but still great looking, for
                    saving ink when printing DIY cards.
                  </p>
                  <p>
                    <label> Border colour </label>
                    <input
                      v-model="localDeck.appearance.borderColour"
                      :disabled="!deck.appearance.isPNP"
                      class="form-control"
                      type="color"
                    />
                  </p>
                </div>
              </div>
            </div>
          </b-collapse>
        </b-col>
      </b-row>
      <!-- Row for character and rules cards -->
      <b-row align-v="end">
        <!-- First character card, is always visible -->
        <b-col
          cols="auto"
          class="py-3"
          :style="{ 'z-index': localDeck.extraCharacters.length + 1 }"
        >
          <h3>
            Character{{
              localDeck.sidekick.quantity || localDeck.extraCharacters.length
                ? "s"
                : ""
            }}
          </h3>
          <ZoomBox :zoomFactor="zoom">
            <UnmatchedCharacterCard
              :isEditable="isEditable"
              :heroName.sync="localDeck.hero.name"
              :heroIsRanged.sync="localDeck.hero.isRanged"
              :heroHp.sync="localDeck.hero.hp"
              :heroMove.sync="localDeck.hero.move"
              :heroSpecialAbility.sync="localDeck.hero.specialAbility"
              :sidekickName.sync="localDeck.sidekick.name"
              :sidekickIsRanged.sync="localDeck.sidekick.isRanged"
              :sidekickHp.sync="localDeck.sidekick.hp"
              :sidekickQuantity.sync="localDeck.sidekick.quantity"
              :sidekickQuote.sync="localDeck.sidekick.quote"
              class="float-left shadow"
            />
          </ZoomBox>
        </b-col>
        <!-- Other character cards -->
        <b-col
          cols="auto"
          class="py-3"
          :style="{ 'z-index': localDeck.extraCharacters.length - index }"
          v-for="(charCard, index) in localDeck.extraCharacters"
          :key="`charcard_${index}`"
        >
          <ZoomBox :zoomFactor="zoom">
            <UnmatchedCharacterCard
              :isEditable="isEditable"
              :isDeletable="true"
              :heroName.sync="charCard.hero.name"
              :heroIsRanged.sync="charCard.hero.isRanged"
              :heroHp.sync="charCard.hero.hp"
              :heroMove.sync="charCard.hero.move"
              :heroSpecialAbility.sync="charCard.hero.specialAbility"
              :sidekickName.sync="charCard.sidekick.name"
              :sidekickIsRanged.sync="charCard.sidekick.isRanged"
              :sidekickHp.sync="charCard.sidekick.hp"
              :sidekickQuantity.sync="charCard.sidekick.quantity"
              :sidekickQuote.sync="charCard.sidekick.quote"
              class="float-left shadow"
              @delete:card="$delete(deck.extraCharacters, index)"
            />
          </ZoomBox>
        </b-col>
        <!-- If editable, show add character card button -->
        <b-col cols="auto" class="py-3" v-if="isEditable">
          <ZoomBox :zoomFactor="zoom">
            <div
              class="unmatched-card blank"
              @click="addCharacterCard"
              style="cursor: pointer"
            >
              <font-awesome-icon :icon="['fas', 'plus-circle']" />
            </div>
          </ZoomBox>
        </b-col>
        <!-- Card back -->
        <b-col
          cols="auto"
          class="py-3"
          v-if="isEditable || localDeck.appearance.cardbackUrl"
        >
          <h3>Card back</h3>
          <ZoomBox :zoomFactor="zoom">
            <UnmatchedCardBack
              v-model="localDeck.appearance.cardbackUrl"
              :isEditable="isEditable"
            />
          </ZoomBox>
        </b-col>
        <!-- Column for all rule cards, so it wraps
          Heading has to be inside card columns for alignment
          with character cards
        -->
        <b-col cols="auto">
          <b-row align-v="end">
            <b-col
              cols="auto"
              class="py-3"
              v-for="(ruleCard, index) in localDeck.ruleCards"
              :key="`rulecard_${index}`"
            >
              <h3 v-if="index === 0">Extra rules</h3>
              <ZoomBox :zoomFactor="zoom">
                <UnmatchedRuleCard
                  :isEditable="isEditable"
                  :title.sync="ruleCard.title"
                  :content.sync="ruleCard.content"
                  @delete:card="$delete(deck.ruleCards, index)"
                  class="float-left shadow"
                />
              </ZoomBox>
            </b-col>

            <div class="col-auto py-3" v-if="isEditable">
              <h3 v-if="localDeck.ruleCards.length === 0">Extra rules</h3>
              <ZoomBox :zoomFactor="zoom">
                <div
                  class="unmatched-card blank"
                  @click="addRuleCard"
                  style="cursor: pointer"
                >
                  <font-awesome-icon :icon="['fas', 'plus-circle']" />
                </div>
              </ZoomBox>
            </div>
          </b-row>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <h3>Deck</h3>
        </b-col>
      </b-row>
      <b-tabs v-model="viewTabIndex">
        <b-tab title="Deck view" lazy>
          <b-row v-if="isEditable" class="pt-2 pl-2">
            <b-col>
              <DeckEditorAddCardButton @add-card="add" />
            </b-col>
          </b-row>
          <draggable
            v-model="fullDeck"
            draggable=".can-drag"
            handle=".drag-handle"
            animation="200"
            :move="onMoveCard"
          >
            <transition-group
              class="row deck-view"
              type="transition"
              :name="!drag ? 'flip-list' : null"
            >
              <div
                v-for="(card, index) in fullDeck"
                :key="card.id"
                class="col-auto py-3 d-flex flex-column align-items-center"
                :class="{ 'can-drag': card.cardNumber === 0 }"
              >
                <ZoomBox :zoomFactor="zoom">
                  <UnmatchedCard
                    :isEditable="isEditable"
                    :deckProperties="deck"
                    :cardType.sync="card.data.type"
                    :cardValue.sync="card.data.value"
                    :cardTitle.sync="card.data.title"
                    :characterName.sync="card.data.characterName"
                    :boostValue.sync="card.data.boost"
                    :basicText.sync="card.data.basicText"
                    :immediateText.sync="card.data.immediateText"
                    :duringText.sync="card.data.duringText"
                    :afterText.sync="card.data.afterText"
                    :imageUrl.sync="card.data.imageUrl"
                    :cardQuantity.sync="card.data.quantity"
                    class="float-left shadow"
                    :class="{ 'over-30': index >= 30 }"
                    @delete:card="$delete(deck.cards, card.origIndex)"
                  />
                </ZoomBox>
                <div v-if="card.cardNumber === 0" class="drag-handle">
                  <font-awesome-icon
                    title="Drag to reorder"
                    :icon="['fal', 'grip-lines']"
                    size="2x"
                  />
                </div>
              </div>
              <div
                class="col-auto py-3"
                v-for="placeholder in remainingCards"
                :key="placeholder.id"
              >
                <ZoomBox :zoomFactor="zoom">
                  <div
                    v-if="placeholder.id === 'add_card'"
                    class="unmatched-card blank"
                    @click="addBlank"
                    style="cursor: pointer"
                  >
                    <font-awesome-icon :icon="['fas', 'plus-circle']" />
                  </div>
                  <div v-else class="unmatched-card blank">
                    {{ placeholder.value }}
                  </div>
                </ZoomBox>
              </div>
              <div
                class="col-auto py-3"
                v-if="isEditable && !remainingCards.length"
                key="extra-add"
              >
                <ZoomBox :zoomFactor="zoom">
                  <div
                    class="unmatched-card blank over-30"
                    @click="addBlank"
                    style="cursor: pointer"
                  >
                    <font-awesome-icon :icon="['fas', 'plus-circle']" />
                  </div>
                </ZoomBox>
              </div>
            </transition-group>
          </draggable>
          <b-row v-if="isEditable" class="pt-2 pl-2">
            <b-col>
              <DeckEditorAddCardButton @add-card="add" />
            </b-col>
          </b-row>
        </b-tab>
        <b-tab title="List view">
          <b-row v-if="isEditable && localDeck.cards.length" class="pt-2 pl-2">
            <b-col>
              <DeckEditorAddCardButton @add-card="add" />
            </b-col>
          </b-row>
          <draggable
            v-model="localDeck.cards"
            animation="200"
            dragClass="being-dragged"
            handle=".drag-handle"
            @start="drag = true"
            @end="drag = false"
          >
            <transition-group
              type="transition"
              :name="!drag ? 'flip-list' : null"
            >
              <CompactCard
                v-for="(card, index) in localDeck.cards"
                :key="card._uuid"
                :isEditable="isEditable"
                :cardType.sync="card.type"
                :cardValue.sync="card.value"
                :cardTitle.sync="card.title"
                :characterName.sync="card.characterName"
                :boostValue.sync="card.boost"
                :basicText.sync="card.basicText"
                :immediateText.sync="card.immediateText"
                :duringText.sync="card.duringText"
                :afterText.sync="card.afterText"
                :imageUrl.sync="card.imageUrl"
                :cardQuantity.sync="card.quantity"
                @delete:card="$delete(deck.cards, index)"
              />
            </transition-group>
          </draggable>
          <b-row v-if="isEditable" class="pt-2 pl-2">
            <b-col>
              <DeckEditorAddCardButton @add-card="add" />
            </b-col>
          </b-row>
        </b-tab>
      </b-tabs>
      <b-row class="mt-3" v-if="localDeck.cards.length">
        <b-col>
          <h3>Deck stats</h3>
          <DeckAnalysis :cards="deck.cards" />
        </b-col>
      </b-row>
      <hr />
      <div class="row py-3 justify-content-center">
        <div class="col-10">
          <h5>A Note on the Type</h5>
          <p>
            The real Unmatched cards are set in
            <a
              href="https://www.typography.com/fonts/knockout/overview"
              target="_blank"
              >Knockout</a
            >
            by Hoefler&Co. This site uses free alternatives.
          </p>
          <p>
            The headings are set in
            <a
              href="https://www.fontsquirrel.com/fonts/bebas-neue"
              target="_blank"
              >Bebas Neue Regular</a
            >. Unfortunately, the free version of Bebas Neue doesn't have
            lowercase glyphs, so places that require them use
            <a
              href="https://www.theleagueofmoveabletype.com/league-gothic"
              target="_blank"
              >League Gothic Regular</a
            >
            instead. Last but not least, the card descriptions are set in
            <a
              href="https://www.fontsquirrel.com/fonts/archivo-narrow"
              target="_blank"
              >Archivo Narrow Regular</a
            >
            for legibility at small sizes.
          </p>
        </div>
      </div>
    </div>

    <div
      class="print"
      :class="{ 'crop-marks': hasCropMarks, 'card-borders': hasCardBorders }"
    >
      <UnmatchedCharacterCard
        :heroName="deck.hero.name"
        :heroIsRanged="deck.hero.isRanged"
        :heroHp="deck.hero.hp"
        :heroMove="deck.hero.move"
        :heroSpecialAbility="deck.hero.specialAbility"
        :sidekickName="deck.sidekick.name"
        :sidekickIsRanged="deck.sidekick.isRanged"
        :sidekickHp="deck.sidekick.hp"
        :sidekickQuantity="deck.sidekick.quantity"
        :sidekickQuote="deck.sidekick.quote"
        :isEditable="false"
        class="float-left"
      />
      <UnmatchedCharacterCard
        v-for="(charCard, index) in localDeck.extraCharacters"
        :key="`charcardprint_${index}`"
        :heroName="charCard.hero.name"
        :heroIsRanged="charCard.hero.isRanged"
        :heroHp="charCard.hero.hp"
        :heroMove="charCard.hero.move"
        :heroSpecialAbility="charCard.hero.specialAbility"
        :sidekickName="charCard.sidekick.name"
        :sidekickIsRanged="charCard.sidekick.isRanged"
        :sidekickHp="charCard.sidekick.hp"
        :sidekickQuantity="charCard.sidekick.quantity"
        :sidekickQuote="charCard.sidekick.quote"
        :isEditable="false"
        class="float-left"
      />
      <UnmatchedRuleCard
        v-for="(ruleCard, index) in localDeck.ruleCards"
        :key="`rulecardprint_${index}`"
        :title="ruleCard.title"
        :content="ruleCard.content"
        :isEditable="false"
        class="float-left"
      />
      <UnmatchedCard
        v-for="card in fullDeck"
        :key="card.id"
        :deckProperties="deck"
        :cardType="card.data.type"
        :cardValue="card.data.value"
        :cardTitle="card.data.title"
        :characterName="card.data.characterName"
        :boostValue="card.data.boost"
        :basicText="card.data.basicText"
        :immediateText="card.data.immediateText"
        :duringText="card.data.duringText"
        :afterText="card.data.afterText"
        :imageUrl="card.data.imageUrl"
        :cardQuantity="card.data.quantity"
        :isEditable="false"
        class="float-left"
      />
      <template v-if="printCardBacks && localDeck.appearance.cardbackUrl">
        <UnmatchedCardBack
          v-for="card in fullDeck"
          :key="`card-back-${card.id}`"
          v-model="localDeck.appearance.cardbackUrl"
          :isEditable="false"
          class="float-left"
        />
      </template>
    </div>
  </div>
</template>

<script>
import BGGLink from "@/components/BGGLink.vue";
import CompactCard from "@/components/CompactCard.vue";
import DeckAnalysis from "@/components/DeckAnalysis.vue";
import DeckEditorAddCardButton from "@/components/DeckEditorAddCardButton.vue";
import SvgBackgroundPicker from "@/components/SvgBackgroundPicker";
import UnmatchedCard from "@/components/UnmatchedCard.vue";
import UnmatchedCardBack from "@/components/UnmatchedCardBack.vue";
import UnmatchedCharacterCard from "@/components/UnmatchedCharacterCard.vue";
import UnmatchedRuleCard from "@/components/UnmatchedRuleCard.vue";
import ZoomBox from "@/components/ZoomBox.vue";

import { best } from "wcag-color";
import draggable from "vuedraggable";
import {
  UnmatchedDeck,
  UnmatchedCard as UMCard,
  UnmatchedCharacter,
} from "@/Unmatched";

export default {
  name: "UnmatchedDeckEditor",
  components: {
    BGGLink,
    CompactCard,
    DeckAnalysis,
    DeckEditorAddCardButton,
    SvgBackgroundPicker,
    UnmatchedCard,
    UnmatchedCardBack,
    UnmatchedCharacterCard,
    UnmatchedRuleCard,
    ZoomBox,
    draggable,
  },
  props: {
    deck: {
      type: UnmatchedDeck,
      required: true,
    },
    isEditable: {
      type: Boolean,
      default: false,
    },
    note: {
      type: String,
      default: "",
    },
    bggLink: {
      type: String,
      default: "",
    },
  },
  data: function () {
    return {
      zoom: 1,
      pattern: "none",
      cardID: 0,
      showAppearance: false,
      showPrint: false,
      drag: false,
    };
  },
  computed: {
    viewTabIndex: {
      get() {
        if ("viewTabIndex" in this.$store.state.userPreferences) {
          return this.$store.state.userPreferences.viewTabIndex;
        }
        return false;
      },
      set(value) {
        this.$store.commit("setPreference", {
          key: "viewTabIndex",
          value: value,
        });
      },
    },
    printCardBacks: {
      get() {
        if ("printCardBacks" in this.$store.state.userPreferences) {
          return this.$store.state.userPreferences.printCardBacks;
        }
        return false;
      },
      set(value) {
        this.$store.commit("setPreference", {
          key: "printCardBacks",
          value: value,
        });
      },
    },
    hasCropMarks: {
      get() {
        if ("hasCropMarks" in this.$store.state.userPreferences) {
          return this.$store.state.userPreferences.hasCropMarks;
        }
        return false;
      },
      set(value) {
        this.$store.commit("setPreference", {
          key: "hasCropMarks",
          value: value,
        });
      },
    },
    hasCardBorders: {
      get() {
        if ("hasCardBorders" in this.$store.state.userPreferences) {
          return this.$store.state.userPreferences.hasCardBorders;
        }
        return false;
      },
      set(value) {
        this.$store.commit("setPreference", {
          key: "hasCardBorders",
          value: value,
        });
      },
    },
    localDeck: {
      get() {
        return this.deck || [];
      },
      set(deck) {
        this.$emit("input", deck);
      },
    },
    localNote: {
      get() {
        return this.note;
      },
      set(note) {
        this.$emit("update:note", note);
      },
    },
    localBggLink: {
      get() {
        return this.bggLink;
      },
      set(bggLink) {
        this.$emit("update:bggLink", bggLink);
      },
    },
    fullDeck: {
      get() {
        var fullDeck = [];
        this.localDeck.cards.forEach((card, outerIndex) => {
          var quantity = parseInt(card.quantity) || 1;
          quantity = quantity > 0 ? quantity : 1;
          [...Array(quantity)].forEach((_, innerIndex) => {
            fullDeck.push({
              data: card,
              id: `${card._uuid}_${innerIndex}`,
              origIndex: outerIndex,
              cardNumber: innerIndex,
              insertPositions:
                quantity === 1
                  ? true
                  : innerIndex === 0
                  ? -1
                  : innerIndex === quantity - 1
                  ? 1
                  : false,
            });
          });
        });
        return fullDeck;
      },
      set(fullDeck) {
        const reorderedDeck = fullDeck
          .filter((x) => x.cardNumber === 0)
          .map((x) => this.localDeck.cards[x.origIndex]);
        this.localDeck.cards = reorderedDeck;
      },
    },
    remainingCards() {
      var remainingCards = [];
      if (this.isEditable) {
        while (this.fullDeck.length + remainingCards.length < 30) {
          const placeholder = this.fullDeck.length + remainingCards.length + 1;
          remainingCards.push({
            id: remainingCards.length
              ? `placeholder_${placeholder}`
              : "add_card",
            value: placeholder,
          });
        }
      }
      return remainingCards;
    },
    userColours() {
      return {
        "--inner-border-colour": this.deck.appearance.isPNP
          ? this.deck.appearance.borderColour
          : "#F7EADB",
        "--outer-border-colour": "#F7EADB",
        "--highlight-colour": this.deck.appearance.highlightColour,
        "--contrast-colour": this.isDarkText(
          this.deck.appearance.highlightColour
        ),
        "--background-pattern": this.pattern,
      };
    },
  },
  methods: {
    addBlank() {
      this.add();
    },
    add: function (card) {
      if (!card) {
        this.localDeck.cards.push(new UMCard({ title: "Title" }));
      } else {
        const newCard = new UMCard({
          title: card.title,
          type: card.type,
          value: card.value,
          afterText: card.afterText,
          basicText: card.basicText,
          duringText: card.duringText,
          immediateText: card.immediateText,
        });
        this.localDeck.cards.push(newCard);
      }
    },
    addRuleCard: function () {
      this.localDeck.ruleCards.push({
        title: "Extra rules",
        content: "This is an extra rule.",
      });
    },
    addCharacterCard: function () {
      this.localDeck.extraCharacters.push(new UnmatchedCharacter());
    },
    isDarkText: function (hex) {
      return best("#fff", "#000", hex);
    },
    bggThreadId: function () {
      return this.$refs.bggLink.threadID;
    },
    onMoveCard(evt) {
      return evt.relatedContext.element?.insertPositions;
    },
  },
};
</script>

<style lang="less">
@font-face {
  font-family: BebasNeueRegular;
  src: url("~@/assets/fonts/BebasNeueRegular-vm3oZ.otf");
  font-weight: normal;
  font-style: normal;
}

@font-face {
  font-family: League Gothic;
  src: url("~@/assets/fonts/LeagueGothic-Regular.otf");
  font-weight: normal;
  font-style: normal;
}

@font-face {
  font-family: Archivo Narrow;
  src: url("~@/assets/fonts/ArchivoNarrow-Regular.otf");
  font-weight: normal;
  font-style: normal;
}

@font-face {
  font-family: Archivo Narrow;
  src: url("~@/assets/fonts/ArchivoNarrow-Bold.otf");
  font-weight: bold;
  font-style: normal;
}

@font-face {
  font-family: Archivo Narrow;
  src: url("~@/assets/fonts/ArchivoNarrow-Italic.otf");
  font-weight: normal;
  font-style: italic;
}

@font-face {
  font-family: Archivo Narrow;
  src: url("~@/assets/fonts/ArchivoNarrow-BoldItalic.otf");
  font-weight: bold;
  font-style: italic;
}

* {
  box-sizing: border-box;
}

@page {
  size: landscape;
}

.flip-list-move {
  transition: transform 0.5s;
}

.flip-list-enter,
.flip-list-leave-to {
  opacity: 0;
}

.flip-list-leave-active {
  position: absolute;
}

.being-dragged {
  background: var(--white);
  @media (prefers-color-scheme: dark) {
    background: #222;
  }
}

.printnplay {
  .unmatched-card {
    border-radius: 0;
    background: var(--inner-border-colour);
  }
}

.unmatched-card {
  position: relative;
  width: 63mm;
  height: 88mm;
  line-height: normal;
  background: var(--outer-border-colour);
  padding: 3mm;
  // border: 1px solid rgba(0,0,0,.125);

  border-radius: 2.5mm;
  &.blank {
    background: #eee;
    color: #ccc;
    @media (prefers-color-scheme: dark) {
      background: #444;
      color: #666;
    }
    &.over-30 {
      background: #f5c6cb;
      color: var(--danger);
    }

    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    text-align: center;
    user-select: none;

    font-size: 32mm;
  }
}

.unmatched-card,
.unmatched-card * {
  font-weight: normal;
  font-family: BebasNeueRegular, sans-serif;
  color-adjust: exact;
}

.appearance .card {
  cursor: pointer;
}

.drag-handle {
  color: #ced4da;
  @media (prefers-color-scheme: dark) {
    color: #515151;
  }
  cursor: grab;
}
</style>
